import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

// The app's highlight colour. `null` = use the stylesheet default (#4361EE
// light / #5B72F0 dark). A custom value is written as an inline `--primary`
// on <html>; every companion token (--primary-hover / -bg / -light) is a
// color-mix of --primary in index.css, so they follow automatically.
// Per-device only — same convention as Theme / Wallpaper.

const STORAGE_KEY = 'accent-color';
export const DEFAULT_ACCENT = '#4361EE';

export const ACCENT_PRESETS: { name: string; value: string }[] = [
  { name: 'Default',   value: DEFAULT_ACCENT },
  { name: 'Royal',     value: '#2563EB' },
  { name: 'Teal',      value: '#0D9488' },
  { name: 'Emerald',   value: '#059669' },
  { name: 'Violet',    value: '#7C3AED' },
  { name: 'Magenta',   value: '#DB2777' },
  { name: 'Rose',      value: '#E11D48' },
  { name: 'Amber',     value: '#D97706' },
  { name: 'Slate',     value: '#475569' },
];

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

interface AccentContextValue {
  /** Current custom accent, or null when using the default. */
  accent: string | null;
  /** Pass a hex string to set a custom accent, or null to reset to default. */
  setAccent: (hex: string | null) => void;
}

const AccentContext = createContext<AccentContextValue | null>(null);

function readStored(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && HEX.test(v) ? v : null;
  } catch {
    return null;
  }
}

function applyAccent(hex: string | null) {
  const root = document.documentElement;
  if (hex) root.style.setProperty('--primary', hex);
  else root.style.removeProperty('--primary');
}

export function AccentProvider({ children }: { children: ReactNode }) {
  const [accent, setAccentState] = useState<string | null>(readStored);

  useEffect(() => {
    applyAccent(accent);
    try {
      if (accent) localStorage.setItem(STORAGE_KEY, accent);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* private mode / quota — ignore */ }
  }, [accent]);

  function setAccent(hex: string | null) {
    if (hex === null) { setAccentState(null); return; }
    const norm = hex.trim();
    if (HEX.test(norm)) setAccentState(norm.toLowerCase());
  }

  return (
    <AccentContext.Provider value={{ accent, setAccent }}>
      {children}
    </AccentContext.Provider>
  );
}

export function useAccent(): AccentContextValue {
  const ctx = useContext(AccentContext);
  if (!ctx) throw new Error('useAccent must be used within an AccentProvider');
  return ctx;
}
